import React, { createContext, useCallback, useEffect, useRef, useState } from "react";

export const UserSessionContext = createContext(null);
const USER_SESSION_STORAGE_KEY = "userSession";

const SESSION_DURATION = 5 * 60 * 60 * 1000;
const MAX_SESSION_DURATION = 5 * 60 * 60 * 1000;

export function UserSessionProvider({ children }) {
  // ---- user session data (persisted to sessionStorage) ----
  const [userSessionState, setUserSessionState] = useState(() => {
    try {
      const storedSession = sessionStorage.getItem(USER_SESSION_STORAGE_KEY);
      return storedSession ? JSON.parse(storedSession) : null;
    } catch {
      return null;
    }
  });

  const setUserSession = useCallback((nextSession) => {
    setUserSessionState(nextSession);

    try {
      if (nextSession) {
        sessionStorage.setItem(
          USER_SESSION_STORAGE_KEY,
          JSON.stringify(nextSession),
        );
      } else {
        sessionStorage.removeItem(USER_SESSION_STORAGE_KEY);
      }
    } catch {
      // Ignore storage failures; context state is still updated in memory.
    }
  }, []);

  // ---- inactivity / max-session expiry ----
  const [sessionExpired, setSessionExpired] = useState(false);
  const [maxSessionExpired, setMaxSessionExpired] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);

  const sessionStartTimeRef = useRef(Date.now());

  const handleLogout = useCallback(() => {
    setUserSession(null);
    sessionStorage.clear();
    setSessionExpired(false);
    setMaxSessionExpired(false);
    window.location.href = "/";
  }, [setUserSession]);

  useEffect(() => {
    const resetInactivityTimer = () => {
      if (sessionExpired || maxSessionExpired) return;

      clearTimeout(window.inactivityTimer);
      window.inactivityTimer = setTimeout(() => {
        setSessionExpired(true);
      }, SESSION_DURATION);
    };

    const intervalId = setInterval(() => {
      const timeElapsed = Date.now() - sessionStartTimeRef.current;
      setElapsedTime(timeElapsed);

      if (timeElapsed >= MAX_SESSION_DURATION) {
        setMaxSessionExpired(true);
        clearInterval(intervalId);
      }
    }, 1000);

    const activityHandler = () => resetInactivityTimer();
    window.addEventListener("mousemove", activityHandler);
    window.addEventListener("keydown", activityHandler);

    resetInactivityTimer();

    return () => {
      clearTimeout(window.inactivityTimer);
      clearInterval(intervalId);
      window.removeEventListener("mousemove", activityHandler);
      window.removeEventListener("keydown", activityHandler);
    };
  }, [sessionExpired, maxSessionExpired]);

  const remainingTime = MAX_SESSION_DURATION - elapsedTime;

  const value = {
    userSession: userSessionState,
    setUserSession,
    sessionExpired,
    maxSessionExpired,
    handleLogout,
    elapsedTime,
    remainingTime,
  };

  return (
    <UserSessionContext.Provider value={value}>
      {children}
    </UserSessionContext.Provider>
  );
}
